import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
    IconStapleBack,
    IconTitle,
    IconChevronDown,
    IconChevronUp
} from '$partials/icons/index.js';

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleContextConsumer,
    CollapsibleHeading
} from '$partials/base/collapsible.jsx';

import { Newsletter } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Footer = ({
    bodyText,
    columns,
    contactLinks,
    copyright,
    hasNewsletterModule,
    heading,
    mailchimpAudienceFormId,
    mailchimpAudienceGroup,
    mailchimpAudienceInterestsCategories,
    mailchimpAudienceListId,
    privacyLinks
}) => {
    // state
    const [ isSmallScreen, setIsSmallScreen ] = useState(true);

    // methods
    const handleResize = () => {
        if (window.innerWidth < 1024 && !isSmallScreen) {
            setIsSmallScreen(true);
        }

        if (window.innerWidth >= 1024 && isSmallScreen) {
            setIsSmallScreen(false);
        }
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });

        handleResize();

        return () =>  window.removeEventListener('resize', handleResize);
    }, [ isSmallScreen ]);

    // render
    return (
        <>
            {!hasNewsletterModule && (
                <section>
                    <Newsletter
                        audienceCmsInterests={mailchimpAudienceInterestsCategories}
                        audienceFormId={mailchimpAudienceFormId}
                        audienceGroup={mailchimpAudienceGroup}
                        audienceListId={mailchimpAudienceListId}
                        bodyText={bodyText}
                        heading={heading} />
                </section>
            )}

            <footer className='bg-peach relative layout-container pt-40 pb-20 md:pt-60 md:pb-30 z-50'>
                <div
                    className='absolute right-20 top-20 sm:right-40 sm:top-40 md:top-60 md:right-60'
                    role='presentation'>
                    <IconStapleBack />
                </div>

                {/* Mobile */}
                {isSmallScreen && (
                    <nav
                        className='block md:hidden'
                        id='secondary-navigation'>
                        {contactLinks && (
                            <address className='md:hidden type-style-4'>
                                <p className='sr-only'>
                                    Contact
                                </p>

                                <div
                                    className='rich-text rich-text--navigation'
                                    dangerouslySetInnerHTML={{ __html: contactLinks }} />
                            </address>
                        )}

                        <hr className='md:hidden border-charcoal mt-40 mb-40 sm:mt-20' />

                        {columns.map((col, idx) => {
                            return (
                                <Collapsible
                                    className='w-full block md:hidden mb-40'
                                    id={'footer-nav-' + idx}
                                    key={idx}>
                                    <CollapsibleContextConsumer>
                                        {({ isOpened }) => (
                                            <div className='relative overflow-hidden'>
                                                <CollapsibleHeading className='w-full'>
                                                    <div className='relative md:flex'>
                                                        <div className='flex justify-between items-start'>
                                                            {col.heading && (
                                                                <h2 className='w-6/12 rich-text rich-text--navigation text-left'>
                                                                    {col.heading}
                                                                </h2>
                                                            )}

                                                            <span className='cursor-pointer'>
                                                                {isOpened ? <IconChevronUp /> : <IconChevronDown/>}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </CollapsibleHeading>

                                                <CollapsibleContent>
                                                    <div
                                                        className='rich-text rich-text--navigation mt-25'
                                                        dangerouslySetInnerHTML={{ __html: col.content }} />
                                                </CollapsibleContent>
                                            </div>
                                        )}
                                    </CollapsibleContextConsumer>
                                </Collapsible>
                            );
                        })}
                    </nav>
                )}

                {/* Desktop */}
                {!isSmallScreen && (
                    <div className='hidden md:block layout-grid-negative-gutter'>
                        <nav id='secondary-navigation'>
                            <div className='type-style-4 flex flex-wrap gap-y-60'>
                                {contactLinks && (
                                    <address className='w-3/12 layout-grid-col-padding not-italic'>
                                        <p className='text-black/50 mb-15'>
                                            Contact
                                        </p>

                                        <div
                                            className='rich-text rich-text--navigation'
                                            dangerouslySetInnerHTML={{ __html: contactLinks }} />
                                    </address>
                                )}

                                {columns?.map((col, idx) => {
                                    return (
                                        <React.Fragment key={idx}>
                                            {idx === 3 && (<div className='w-3/12 layout-grid-col-padding' />)}

                                            <div className='w-3/12 layout-grid-col-padding'>
                                                <p className='text-black/50 mb-15'>
                                                    {col.heading}
                                                </p>

                                                <div
                                                    className='rich-text rich-text--navigation'
                                                    dangerouslySetInnerHTML={{ __html: col.content }} />
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </nav>
                    </div>
                )}

                <hr className='md:hidden border-charcoal mt-20 mb-40 sm:mt-20' />

                <div
                    className='flex justify-center md:mt-70'
                    role='presentation'>
                    <IconTitle />
                </div>

                <div className='mt-20 md:mt-30 text-black'>
                    <div className='flex flex-wrap sm:flex-nowrap gap-15 items-center justify-center sm:gap-40 md:gap-20'>
                        <p className='hidden md:block md:w-fit '>
                            {copyright}
                        </p>

                        <ul className='contents'>
                            <div
                                className='contents rich-text rich-text--navigation rich-text--no-m'
                                dangerouslySetInnerHTML={{ __html: privacyLinks }} />
                        </ul>
                    </div>

                    <p className='flex items-center justify-center mt-20 md:hidden'>
                        {copyright}
                    </p>
                </div>
            </footer>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    bodyText: PropTypes.string,
    columns: PropTypes.array,
    contactLinks: PropTypes.string,
    copyright: PropTypes.string,
    hasNewsletterModule: PropTypes.object,
    heading: PropTypes.string,
    mailchimpAudienceFormId: PropTypes.string,
    mailchimpAudienceGroup: PropTypes.string,
    mailchimpAudienceInterestsCategories: PropTypes.string,
    mailchimpAudienceListId: PropTypes.string,
    privacyLinks: PropTypes.string
};

Footer.propTypes = propTypes;

export default Footer;
